import {
    MTJ__SINGLE_END_A,
    MTJ__SINGLE_END_B,
    MTJ__SINGLE_END_A_THEN_END_B,
    MTJ__DOUBLE,
    TEST_PLAN_TEMPLATE__SIMPLEX,
    TEST_PLAN_TEMPLATE__DUPLEX,
    ASSEMBLY_TYPE__MULTI_CABLE,
    ASSEMBLY_TYPE__SINGLE_CABLE,
    TEST_PLAN_TEMPLATE__MULTIFIBER,
    TEST_PLAN_TEMPLATE__FAN_OUT,
    NEXT_CABLE,
    FLIP_ASSEMBLY
} from '@shared/constants/constants';

export function generateDefault(
    fibersPerCable,
    cablesPerAssembly,
    templateType,
    hybridAssembly,
    assemblyType,
    mtjConfiguration,
    singleTestBlock
) {
    let totalFibers = fibersPerCable * cablesPerAssembly;
    let fibers: string[] = [];
    let switch0: (number | null)[] = [];
    let switch1: (number | null)[] = [];
    let switch2: (number | null)[] = [];
    let tj1Length: (number | null)[] = [];
    let tj2Length: (number | null)[] = [];
    let breakCable: { [key: string]: boolean } = {};

    // FIBERS

    if (mtjConfiguration === MTJ__SINGLE_END_A || mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
        for (let i = 1; i <= totalFibers; i++) {
            fibers.push(i + 'a');
            breakCable[i + 'a'] =
                i % fibersPerCable === 1 || fibersPerCable === 1 || templateType === TEST_PLAN_TEMPLATE__FAN_OUT ? true : false;
            tj1Length.push(null);
            tj2Length.push(null);
        }
    }

    if (mtjConfiguration === MTJ__SINGLE_END_B || mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
        if (templateType === TEST_PLAN_TEMPLATE__FAN_OUT) {
            let counter = fibersPerCable;

            breakCable['1b'] = true;
            for (let i = 1; i <= totalFibers; i++) {
                if (i === counter + 1) {
                    breakCable[i + 'b'] = true;
                    counter = counter + fibersPerCable;
                }
                fibers.push(i + 'b');
                tj1Length.push(null);
                tj2Length.push(null);
            }
        } else {
            for (let i = 1; i <= totalFibers; i++) {
                fibers.push(i + 'b');
                breakCable[i + 'b'] =
                    i % fibersPerCable === 1 ||
                    fibersPerCable === 1 ||
                    (templateType === TEST_PLAN_TEMPLATE__FAN_OUT && mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B)
                        ? true
                        : false;
                tj1Length.push(null);
                tj2Length.push(null);
            }
        }
    }

    if (mtjConfiguration === MTJ__DOUBLE) {
        for (let i = 1; i <= totalFibers; i++) {
            fibers.push(i + 'a/b');
            breakCable[i + 'a/b'] = i % fibersPerCable === 1 || fibersPerCable === 1 ? true : false;
            tj1Length.push(null);
            tj2Length.push(null);
        }
    }

    // SWITCH 0 / SWITCH 1 / SWITCH 2 / PAUSE BEFORE
    let pauseMessages = Array(fibers.length).fill('pause');
    let pauseBefore = Array(fibers.length).fill(false);

    if (templateType === TEST_PLAN_TEMPLATE__SIMPLEX) {
        // SWITCH 0

        if (mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B && hybridAssembly) {
            // First half fibers on 1, second half on 2
            switch0 = Array(fibers.length)
                .fill(1, 0, fibers.length / 2)
                .fill(2, fibers.length / 2);
        } else {
            switch0 = Array(fibers.length).fill(1);
        }

        // SWITCH 1

        // Simplex cables don't use switch 1
        // switch1 = Array(fibers.length).fill(null);

        switch1 = Array.from({ length: fibers.length }, (_, i) => i + 1);

        // SWITCH 2

        switch2 = Array.from({ length: fibers.length }, (_, i) => i + 1);

        // PAUSE BEFORE

        for (let i = fibersPerCable; i < totalFibers; i += fibersPerCable) {
            pauseBefore[i] = true;
        }

        if (mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
            for (let i = 0; i < totalFibers; i += fibersPerCable) {
                pauseBefore[totalFibers + i] = true;
            }
        }

        // PAUSE MESSAGE

        if (assemblyType === ASSEMBLY_TYPE__SINGLE_CABLE && mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
            pauseMessages[1] = FLIP_ASSEMBLY;
        }

        if (assemblyType === ASSEMBLY_TYPE__MULTI_CABLE && mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
            pauseMessages = Array(fibers.length).fill(NEXT_CABLE);

            pauseMessages[fibers.length / 2] = FLIP_ASSEMBLY;
        }
        if (
            assemblyType === ASSEMBLY_TYPE__MULTI_CABLE &&
            (mtjConfiguration === MTJ__SINGLE_END_A || mtjConfiguration === MTJ__SINGLE_END_B)
        ) {
            pauseMessages = Array(fibers.length).fill(NEXT_CABLE, 1);
        }

        if (assemblyType === ASSEMBLY_TYPE__MULTI_CABLE && mtjConfiguration === MTJ__DOUBLE) {
            pauseMessages = Array(fibers.length).fill(NEXT_CABLE, 1);
        }
    } else if (templateType === TEST_PLAN_TEMPLATE__DUPLEX) {
        if (mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B && hybridAssembly) {
            // SWITCH 0

            switch0 = Array(fibers.length).fill(1);

            // SWITCH 1/2

            // First half alternates between 1 and 2
            // (even => 1, odd => 2)
            for (let i = 0; i < fibers.length / 2; i++) {
                if (i % 2 === 0) {
                    switch1.push(1);
                    switch2.push(1);
                } else {
                    switch1.push(2);
                    switch2.push(2);
                }
            }

            // Second half alternates between 3 and 4
            // (even => 3, odd => 4)
            for (let i = 0; i < fibers.length / 2; i++) {
                if (i % 2 === 0) {
                    switch1.push(3);
                    switch2.push(3);
                } else {
                    switch1.push(4);
                    switch2.push(4);
                }
            }
        } else {
            // SWITCH 0/1/2

            // Switch 0 alternates between 1 and 2
            // (even => 1, odd => 2)
            for (let i = 0; i < fibers.length; i++) {
                if (i % 2 === 0) {
                    switch0.push(1);
                    switch1.push(1);
                    switch2.push(1);
                } else {
                    switch0.push(2);
                    switch1.push(2);
                    switch2.push(2);
                }
            }
        }

        // PAUSE BEFORE

        for (let i = fibersPerCable; i < totalFibers; i += fibersPerCable) {
            pauseBefore[i] = true;
        }

        if (mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
            for (let i = 0; i < totalFibers; i += fibersPerCable) {
                pauseBefore[totalFibers + i] = true;
            }
        }

        //PAUSE MESSAGES
        if (assemblyType === ASSEMBLY_TYPE__SINGLE_CABLE && mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
            pauseMessages = Array(fibers.length).fill('');

            pauseMessages[fibers.length / 2] = FLIP_ASSEMBLY;
        }
        if (assemblyType === ASSEMBLY_TYPE__MULTI_CABLE && mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
            pauseMessages = Array(fibers.length).fill('');
            for (let i = 1; i < fibers.length - 1; i = i + 2) {
                pauseMessages[i + 1] = NEXT_CABLE;
            }
            pauseMessages[fibers.length / 2] = FLIP_ASSEMBLY;
        }

        if (
            assemblyType === ASSEMBLY_TYPE__MULTI_CABLE &&
            (mtjConfiguration === MTJ__SINGLE_END_A || mtjConfiguration === MTJ__SINGLE_END_B)
        ) {
            pauseMessages = Array(fibers.length).fill('');
            for (let i = 1; i < fibers.length - 1; i = i + 2) {
                pauseMessages[i + 1] = NEXT_CABLE;
            }
        }

        if (assemblyType === ASSEMBLY_TYPE__MULTI_CABLE && mtjConfiguration === MTJ__DOUBLE) {
            pauseMessages = Array(fibers.length).fill('');
            for (let i = 1; i < fibers.length - 1; i = i + 2) {
                pauseMessages[i + 1] = NEXT_CABLE;
            }
        }
    } else if (templateType === TEST_PLAN_TEMPLATE__MULTIFIBER) {
        // SWITCH 0
        switch0 = Array(fibers.length).fill(1);

        // SWITCH 1/2

        for (let i = 0; i < cablesPerAssembly; i++) {
            for (let j = 1; j <= fibersPerCable; j++) {
                switch1.push(j);
                switch2.push(j);
            }
        }

        if (mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
            if (hybridAssembly) {
                for (let i = 0; i < cablesPerAssembly; i++) {
                    for (let j = 1; j <= fibersPerCable; j++) {
                        switch1.push(fibersPerCable + j);
                        switch2.push(fibersPerCable + j);
                    }
                }
            } else {
                for (let i = 0; i < cablesPerAssembly; i++) {
                    for (let j = 1; j <= fibersPerCable; j++) {
                        switch1.push(j);
                        switch2.push(j);
                    }
                }
            }
        }

        // PAUSE BEFORE

        for (let i = fibersPerCable; i < totalFibers; i += fibersPerCable) {
            pauseBefore[i] = true;
        }

        if (mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
            for (let i = 0; i < totalFibers; i += fibersPerCable) {
                pauseBefore[totalFibers + i] = true;
            }
        }

        //PAUSE MESSAGES
        if (assemblyType === ASSEMBLY_TYPE__SINGLE_CABLE && mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
            pauseMessages = Array(fibers.length).fill('');

            pauseMessages[fibers.length / 2] = FLIP_ASSEMBLY;
        }
        if (assemblyType === ASSEMBLY_TYPE__MULTI_CABLE && mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
            pauseMessages = Array(fibers.length).fill('');
            for (let i = fibersPerCable; i < pauseMessages.length; i += fibersPerCable) {
                pauseMessages[i] = NEXT_CABLE;
            }
            pauseMessages[fibers.length / 2] = FLIP_ASSEMBLY;
        }
        if (
            assemblyType === ASSEMBLY_TYPE__MULTI_CABLE &&
            (mtjConfiguration === MTJ__SINGLE_END_A || mtjConfiguration === MTJ__SINGLE_END_B)
        ) {
            for (let i = fibersPerCable; i < pauseMessages.length; i += fibersPerCable) {
                pauseMessages = Array(fibers.length).fill('');
                pauseMessages[i] = NEXT_CABLE;
            }
        }

        if (
            assemblyType === ASSEMBLY_TYPE__MULTI_CABLE &&
            (mtjConfiguration === MTJ__SINGLE_END_A || mtjConfiguration === MTJ__SINGLE_END_B)
        ) {
            pauseMessages = Array(fibers.length).fill('');
            for (let i = fibersPerCable; i < pauseMessages.length; i += fibersPerCable) {
                pauseMessages[i] = NEXT_CABLE;
            }
        }
        if (assemblyType === ASSEMBLY_TYPE__MULTI_CABLE && mtjConfiguration === MTJ__DOUBLE) {
            pauseMessages = Array(fibers.length).fill('');
            for (let i = fibersPerCable; i < pauseMessages.length; i += fibersPerCable) {
                pauseMessages[i] = NEXT_CABLE;
            }
        }
    } else if (templateType === TEST_PLAN_TEMPLATE__FAN_OUT) {
        // SWITCH 0
        switch0 = Array(fibers.length).fill(1);
        if (mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B && cablesPerAssembly === 1) {
            // SWITCH 1/2
            for (let i = 0; i < cablesPerAssembly; i++) {
                for (let j = 1; j <= fibersPerCable * 2; j++) {
                    switch1.push(j);
                    switch2.push(j);
                }
            }
        } else if (mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B && cablesPerAssembly !== 1) {
            for (let i = 1; i <= cablesPerAssembly * fibersPerCable * 2; i++) {
                switch1.push(i);
                switch2.push(i);
            }
        }

        if ((mtjConfiguration === MTJ__SINGLE_END_A || mtjConfiguration === MTJ__SINGLE_END_B) && cablesPerAssembly === 1) {
            // SWITCH 1

            for (let j = 1; j <= fibersPerCable; j++) {
                switch1.push(j);
                switch2.push(j);
            }
        } else if (
            (mtjConfiguration === MTJ__SINGLE_END_A || mtjConfiguration === MTJ__SINGLE_END_B) &&
            cablesPerAssembly !== 1
        ) {
            for (let i = 1; i <= cablesPerAssembly * fibersPerCable; i++) {
                switch1.push(i);
                switch2.push(i);
            }
        }

        if (mtjConfiguration === MTJ__DOUBLE && cablesPerAssembly === 1) {
            // SWITCH 1/2
            for (let j = 1; j <= fibersPerCable; j++) {
                switch1.push(j);
                switch2.push(j);
            }
        } else if (mtjConfiguration === MTJ__DOUBLE && cablesPerAssembly !== 1) {
            for (let i = 1; i <= cablesPerAssembly * fibersPerCable; i++) {
                switch1.push(i);
                switch2.push(i);
            }
        }

        // if (
        //     mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B
        //     //&& cablesPerAssembly === 1
        // ) {
        //     //  let factor2 = 1;

        //     for (let i = 0; i < cablesPerAssembly; i++) {
        //         for (let j = 1; j <= fibersPerCable; j++) {
        //             switch1.push(j);
        //         }
        //         // factor2++;
        //     }
        // }
        // else if(mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B && cablesPerAssembly > 1) {
        // }

        // PAUSE BEFORE

        // No pauses for for MTJ__SINGLE_END_A or MTJ__DOUBLE
        if (mtjConfiguration === MTJ__SINGLE_END_B) {
            if (assemblyType === ASSEMBLY_TYPE__MULTI_CABLE) {
                for (let i = fibersPerCable; i < totalFibers; i += fibersPerCable) {
                    pauseBefore[i] = true;
                }
            } else {
                for (let i = 1; i < totalFibers; i++) {
                    pauseBefore[i] = true;
                }
            }
        } else if (mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
            if (assemblyType === ASSEMBLY_TYPE__MULTI_CABLE) {
                pauseMessages = Array(fibers.length).fill('');

                if (hybridAssembly) {
                    for (let i = totalFibers; i < totalFibers * 2; i = i + fibersPerCable) {
                        pauseBefore[i] = true;
                        pauseMessages[i] = NEXT_CABLE;
                    }
                } else {
                    for (let i = 0; i < totalFibers; i++) {
                        pauseBefore[totalFibers + i] = true;
                    }
                }
            } else {
                for (let i = 0; i < totalFibers; i++) {
                    pauseBefore[totalFibers + i] = true;
                }
            }
        } else if (mtjConfiguration === MTJ__DOUBLE) {
            if (hybridAssembly) {
                for (let i = fibersPerCable; i < totalFibers; i += fibersPerCable) {
                    pauseBefore[i] = true;
                    pauseMessages[i] = NEXT_CABLE;
                }
            }
        }

        //PAUSE MESSAGES
        if (assemblyType === ASSEMBLY_TYPE__SINGLE_CABLE && mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
            pauseMessages = Array(fibers.length).fill(NEXT_CABLE, fibers.length / 2);

            pauseMessages[fibers.length / 2] = FLIP_ASSEMBLY;
        }

        if (assemblyType === ASSEMBLY_TYPE__SINGLE_CABLE && mtjConfiguration === MTJ__SINGLE_END_A) {
            pauseMessages = Array(fibers.length).fill('');
        }
        if (assemblyType === ASSEMBLY_TYPE__SINGLE_CABLE && mtjConfiguration === MTJ__SINGLE_END_B) {
            pauseMessages = Array(fibers.length).fill('');
            for (let i = 1; i < totalFibers; i++) {
                pauseMessages[i] = NEXT_CABLE;
            }
        }
        if (assemblyType === ASSEMBLY_TYPE__MULTI_CABLE && mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B) {
            pauseMessages = Array(fibers.length).fill('');

            let factor = fibersPerCable;
            if (fibersPerCable === 1) {
                factor = 2;
            }
            if (hybridAssembly) {
                for (let i = fibersPerCable; i < totalFibers * factor; i += fibersPerCable) {
                    pauseMessages[i] = NEXT_CABLE;
                }
            } else {
                for (let i = fibers.length / 2; i < pauseMessages.length; i += fibersPerCable) {
                    pauseMessages[i] = NEXT_CABLE;
                }
            }
            pauseMessages[fibers.length / 2] = FLIP_ASSEMBLY;
        }
        if (assemblyType === ASSEMBLY_TYPE__MULTI_CABLE && mtjConfiguration === MTJ__SINGLE_END_A) {
            pauseMessages = Array(fibers.length).fill('');
        }
    }

    // When split between two test blocks (AA...BB), the second test block must not start with a pause
    if (mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B && !singleTestBlock) {
        pauseBefore[totalFibers] = false;
    }

    return {fibers, switch0, switch1, switch2, pauseBefore, pauseMessages, tj1Length, tj2Length, breakCable};
}

export function generateDefaultDetectorMap(detectorPerCable, fibersPerCable, cablesPerAssembly, mtjConfiguration) {
    let totalFibers = fibersPerCable * cablesPerAssembly;
    let endsTested = mtjConfiguration === MTJ__SINGLE_END_A_THEN_END_B ? 2 : 1;

    if (!detectorPerCable) {
        // Every fiber uses detector 1
        return Array(totalFibers * endsTested).fill(1);
    }

    let detectors: number[] = [];

    // For each end
    for (let end = 0; end < endsTested; end++) {
        for (let cable = 0; cable < cablesPerAssembly; cable++) {
            for (let fiber = 0; fiber < fibersPerCable; fiber++) {
                detectors.push(cable + 1);
            }
        }
    }

    return detectors;
}
